import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Snackbar,
  Skeleton,
  Chip,
  InputAdornment,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Alert } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import makeStyles from "@mui/styles/makeStyles";
import {
  fetchNodesWithWhiteLabel,
  fetchCompanyUsers,
  updateWhiteLabel,
  deleteCompanyWhiteLabel,
  saveCompanyExpiredDate,
  saveSalesEmail,
  fetchCompanyTags,
  saveCompanyTags,
  fetchFeatureGroupOptions,
  fetchCompanyFeature,
  saveCompanyFeature,
  fetchFeatureStructure,
  fetchCompanyPlanInfo,
  fetchAvailablePlans,
  swapCompanyPlan,
  fetchIndustries,
} from "../redux/companies.action";
import CompanyFeaturePanel from "../../approvals/components/CompanyFeaturePanel";

const tokens = {
  brand: "#3A4D54",
  brandHover: "#2C3B41",
  brandSoft: "rgba(58,77,84,0.06)",
  brandSofter: "rgba(58,77,84,0.03)",
  text: "#1F2A2F",
  textDim: "#5C6D74",
  textMuted: "#98A2B3",
  border: "#E8ECEE",
  borderStrong: "#D7DCDF",
  surface: "#FFFFFF",
  surfaceAlt: "#F7F9FA",
  surfaceSidebar: "#FBFCFC",
  rowHover: "#F2F5F6",
  successBg: "#ECFDF3",
  successFg: "#067647",
  dangerBg: "#FEF3F2",
  dangerFg: "#B42318",
  dangerBorder: "#FECDCA",
  warningFg: "#B54708",
  infoBg: "#EFF4FF",
  infoFg: "#2D5BD7",
  infoBorder: "#C7D7FE",
  fieldHeight: 38,
};

const TAB_DEFS = [
  { label: "Overview", icon: BusinessRoundedIcon },
  { label: "Configuration", icon: TuneRoundedIcon },
  { label: "Features", icon: TuneRoundedIcon },
  { label: "Branches", icon: StorefrontRoundedIcon },
  { label: "Users", icon: PeopleAltRoundedIcon },
  { label: "Expiry", icon: EventBusyRoundedIcon },
  { label: "Sales Email", icon: EmailRoundedIcon },
  { label: "Swap Plan", icon: SwapHorizRoundedIcon },
];

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 10,
    overflow: "hidden",
  },
  titleRoot: {
    padding: "16px 20px",
    borderBottom: `1px solid ${tokens.border}`,
    backgroundColor: tokens.surface,
  },
  titleEyebrow: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: tokens.textMuted,
    lineHeight: 1.4,
  },
  titleHeading: {
    fontSize: 16,
    fontWeight: 600,
    color: tokens.text,
    lineHeight: 1.35,
    marginTop: 2,
  },
  closeBtn: {
    width: 32,
    height: 32,
    color: tokens.textDim,
    borderRadius: 6,
    "&:hover": { backgroundColor: tokens.brandSoft, color: tokens.brand },
  },
  contentRoot: {
    padding: 0,
    backgroundColor: tokens.surface,
  },
  shell: {
    display: "flex",
    minHeight: 540,
  },
  sidebar: {
    width: 208,
    flexShrink: 0,
    borderRight: `1px solid ${tokens.border}`,
    backgroundColor: tokens.surfaceSidebar,
    padding: "12px 8px",
  },
  navItem: {
    borderRadius: 6,
    padding: "8px 10px",
    marginBottom: 2,
    color: tokens.textDim,
    transition: "background-color 160ms ease, color 160ms ease",
    "&:hover": { backgroundColor: tokens.brandSoft, color: tokens.brand },
    "& .MuiListItemText-primary": {
      fontSize: 13,
      fontWeight: 500,
    },
  },
  navItemActive: {
    backgroundColor: tokens.brandSoft,
    color: tokens.brand,
    "& .MuiListItemText-primary": { fontWeight: 600 },
    "&:hover": { backgroundColor: tokens.brandSoft, color: tokens.brand },
  },
  navIcon: {
    width: 18,
    height: 18,
    marginRight: 10,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    minWidth: 0,
    padding: "24px 28px 28px",
    backgroundColor: tokens.surface,
    overflowY: "auto",
    maxHeight: "75vh",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 14,
    color: tokens.text,
  },
  sectionIcon: {
    width: 16,
    height: 16,
    color: tokens.textDim,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: tokens.textDim,
  },
  defList: {
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    rowGap: 10,
    columnGap: 16,
    marginBottom: 28,
  },
  defLabel: {
    fontSize: 13,
    color: tokens.textDim,
    paddingTop: 1,
  },
  defValue: {
    fontSize: 13,
    color: tokens.text,
    fontWeight: 500,
    overflowWrap: "anywhere",
  },
  defEmpty: {
    color: tokens.textMuted,
    fontWeight: 400,
  },
  docRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  docBtn: {
    color: tokens.brand,
    borderColor: tokens.borderStrong,
    textTransform: "none",
    fontWeight: 500,
    paddingInline: 14,
    height: 36,
    "&:hover": {
      borderColor: tokens.brand,
      backgroundColor: tokens.brandSoft,
    },
  },
  field: {
    "& .MuiOutlinedInput-root": {
      height: tokens.fieldHeight,
      fontSize: 14,
      backgroundColor: tokens.surface,
      "& fieldset": { borderColor: tokens.border },
      "&:hover fieldset": { borderColor: tokens.brand },
      "&.Mui-focused fieldset": { borderColor: tokens.brand, borderWidth: 1 },
    },
  },
  helper: {
    fontSize: 12,
    color: tokens.textDim,
    marginTop: 6,
    lineHeight: 1.5,
  },
  primaryBtn: {
    backgroundColor: tokens.brand,
    color: "#FFFFFF",
    height: tokens.fieldHeight,
    paddingInline: 18,
    boxShadow: "none",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": { backgroundColor: tokens.brandHover, boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: tokens.brandSoft,
      color: tokens.textMuted,
    },
  },
  ghostBtn: {
    color: tokens.textDim,
    height: tokens.fieldHeight,
    paddingInline: 14,
    textTransform: "none",
    fontWeight: 500,
    border: `1px solid ${tokens.borderStrong}`,
    backgroundColor: tokens.surface,
    "&:hover": {
      borderColor: tokens.brand,
      backgroundColor: tokens.brandSoft,
      color: tokens.brand,
    },
  },
  dangerBtn: {
    color: tokens.dangerFg,
    height: tokens.fieldHeight,
    paddingInline: 14,
    textTransform: "none",
    fontWeight: 500,
    border: `1px solid ${tokens.dangerBorder}`,
    backgroundColor: tokens.surface,
    "&:hover": {
      backgroundColor: tokens.dangerBg,
      borderColor: tokens.dangerFg,
    },
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    marginTop: 16,
  },
  statRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 24,
  },
  stat: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    padding: "14px 16px",
    backgroundColor: tokens.surfaceAlt,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: tokens.textDim,
    marginBottom: 6,
  },
  statValue: {
    fontSize: 18,
    fontWeight: 600,
    color: tokens.text,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.01em",
  },
  statValueDim: {
    color: tokens.textMuted,
    fontWeight: 500,
  },
  presetRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  presetChip: {
    height: 32,
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text,
    backgroundColor: tokens.surface,
    border: `1px solid ${tokens.borderStrong}`,
    "&:hover": {
      backgroundColor: tokens.brandSoft,
      borderColor: tokens.brand,
      color: tokens.brand,
    },
  },
  presetChipActive: {
    backgroundColor: tokens.brand,
    color: "#FFFFFF",
    borderColor: tokens.brand,
    "&:hover": {
      backgroundColor: tokens.brandHover,
      borderColor: tokens.brandHover,
      color: "#FFFFFF",
    },
  },
  preview: {
    border: `1px solid ${tokens.infoBorder}`,
    backgroundColor: tokens.infoBg,
    borderRadius: 8,
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 14,
  },
  previewLabel: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: tokens.infoFg,
  },
  previewArrow: {
    color: tokens.infoFg,
    width: 16,
    height: 16,
    margin: "0 4px",
  },
  previewValue: {
    fontSize: 14,
    fontWeight: 600,
    color: tokens.infoFg,
    fontVariantNumeric: "tabular-nums",
  },
  searchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      height: tokens.fieldHeight,
      fontSize: 14,
      backgroundColor: tokens.surface,
      "& fieldset": { borderColor: tokens.border },
      "&:hover fieldset": { borderColor: tokens.brand },
      "&.Mui-focused fieldset": { borderColor: tokens.brand, borderWidth: 1 },
    },
  },
  table: {
    "& .MuiTableCell-root": {
      borderBottomColor: tokens.border,
      fontSize: 13,
      color: tokens.text,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: tokens.textDim,
      backgroundColor: tokens.surface,
      paddingTop: 10,
      paddingBottom: 10,
      whiteSpace: "nowrap",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: tokens.rowHover,
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  pagination: {
    "& .MuiTablePagination-toolbar": {
      paddingInline: 0,
      color: tokens.textDim,
      fontSize: 13,
      minHeight: 44,
    },
  },
  emptyWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: "48px 16px",
    color: tokens.textDim,
  },
  emptyIcon: {
    width: 22,
    height: 22,
    color: tokens.textMuted,
    backgroundColor: tokens.brandSoft,
    borderRadius: "50%",
    padding: 12,
    boxSizing: "content-box",
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 500,
    color: tokens.text,
  },
  emptyHint: {
    fontSize: 12,
    color: tokens.textDim,
  },
  subBlock: {
    marginBottom: 32,
  },
  readonlyField: {
    "& .MuiOutlinedInput-root": {
      height: tokens.fieldHeight,
      fontSize: 13,
      backgroundColor: tokens.surfaceAlt,
      "& fieldset": { borderColor: tokens.border },
      "& input": {
        cursor: "default",
        fontVariantNumeric: "tabular-nums",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        color: tokens.text,
      },
    },
  },
  tagBag: {
    minHeight: 56,
    padding: "10px 12px",
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    backgroundColor: tokens.surfaceAlt,
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    marginBottom: 12,
  },
  tagBagEmpty: {
    color: tokens.textMuted,
    fontStyle: "italic",
    fontSize: 13,
    width: "100%",
    textAlign: "center",
  },
  tagChip: {
    height: 28,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: tokens.brandSoft,
    color: tokens.brand,
    border: `1px solid ${tokens.border}`,
    "& .MuiChip-deleteIcon": {
      color: tokens.textDim,
      "&:hover": { color: tokens.dangerFg },
    },
  },
  subTabs: {
    minHeight: 36,
    marginBottom: 16,
    borderBottom: `1px solid ${tokens.border}`,
    "& .MuiTabs-indicator": {
      backgroundColor: tokens.brand,
      height: 2,
    },
    "& .MuiTab-root": {
      minHeight: 36,
      padding: "6px 14px",
      fontSize: 13,
      fontWeight: 500,
      textTransform: "none",
      color: tokens.textDim,
      letterSpacing: "0.01em",
      "&.Mui-selected": { color: tokens.brand, fontWeight: 600 },
    },
  },
  featurePanelWrap: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    backgroundColor: tokens.surfaceAlt,
    padding: 12,
    maxHeight: 360,
    overflowY: "auto",
  },
  toolRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 4,
    marginBottom: 6,
  },
  textBtnPrimary: {
    color: tokens.brand,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { backgroundColor: tokens.brandSoft },
  },
  textBtnDanger: {
    color: tokens.dangerFg,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { backgroundColor: tokens.dangerBg },
  },
  propertiesGrid: {
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    gap: 12,
    alignItems: "center",
  },
  propertiesGridSplit: {
    display: "grid",
    gridTemplateColumns: "180px 1fr 1fr",
    gap: 12,
    alignItems: "center",
  },
  propertyLabel: {
    fontSize: 13,
    color: tokens.text,
  },
}));

// --- Main Dialog ---
export default function CompanyDetailsDialog({ selectedCompany, onClose }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const [hasChanges, setHasChanges] = useState(false);

  const markChanged = () => setHasChanges(true);
  const handleClose = () => onClose(hasChanges);

  if (!selectedCompany) return null;
  const { company, owner } = selectedCompany;

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle className={classes.titleRoot} component="div">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography component="div" className={classes.titleEyebrow}>Company</Typography>
            <Typography component="h2" className={classes.titleHeading}>{company.name}</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} className={classes.closeBtn} aria-label="Close">
            <CloseRoundedIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers={false} className={classes.contentRoot}>
        <Box className={classes.shell}>
          <Box className={classes.sidebar}>
            <List disablePadding>
              {TAB_DEFS.map((def, i) => {
                const Icon = def.icon;
                const active = tab === i;
                return (
                  <ListItemButton
                    key={def.label}
                    onClick={() => setTab(i)}
                    className={`${classes.navItem} ${active ? classes.navItemActive : ""}`}
                    aria-selected={active}
                  >
                    <Icon className={classes.navIcon} />
                    <ListItemText primary={def.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
          <Box className={classes.content}>
            {tab === 0 && <OverviewTab company={company} owner={owner} classes={classes} />}
            {tab === 1 && <ConfigurationTab company={company} dispatch={dispatch} setSnack={setSnack} onChanged={markChanged} classes={classes} />}
            {tab === 2 && <FeaturesTab company={company} dispatch={dispatch} setSnack={setSnack} onChanged={markChanged} classes={classes} />}
            {tab === 3 && <BranchesTab company={company} dispatch={dispatch} classes={classes} />}
            {tab === 4 && <UsersTab company={company} dispatch={dispatch} classes={classes} />}
            {tab === 5 && <ExpiryTab company={company} dispatch={dispatch} setSnack={setSnack} onChanged={markChanged} classes={classes} />}
            {tab === 6 && <SalesEmailTab company={company} dispatch={dispatch} setSnack={setSnack} onChanged={markChanged} classes={classes} />}
            {tab === 7 && <SwapPlanTab company={company} dispatch={dispatch} setSnack={setSnack} onChanged={markChanged} classes={classes} />}
          </Box>
        </Box>
      </DialogContent>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ fontSize: 13 }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

// --- Definition list helpers ---
function DefRow({ label, value, classes, mono }) {
  const isEmpty = value === undefined || value === null || value === "" || value === "-";
  return (
    <>
      <Box className={classes.defLabel}>{label}</Box>
      <Box
        className={`${classes.defValue} ${isEmpty ? classes.defEmpty : ""}`}
        style={mono ? { fontVariantNumeric: "tabular-nums" } : undefined}
      >
        {isEmpty ? "—" : value}
      </Box>
    </>
  );
}

function Section({ icon: Icon, title, classes, children }) {
  return (
    <Box>
      <Box className={classes.sectionHeader}>
        {Icon && <Icon className={classes.sectionIcon} />}
        <Typography className={classes.sectionTitle}>{title}</Typography>
      </Box>
      {children}
    </Box>
  );
}

// --- Tab 0: Overview ---
function OverviewTab({ company, owner, classes }) {
  const dispatch = useDispatch();
  const industries = useSelector((state) => state.companies.industries);

  useEffect(() => {
    dispatch(fetchIndustries());
  }, [dispatch]);

  const industryName = useMemo(() => {
    const raw = company.industry;
    if (raw === null || raw === undefined || raw === "") return "";
    const match = industries?.find((i) => Number(i.id) === Number(raw));
    if (match?.name) return match.name;
    // Numeric id with no match yet (still loading or unknown id) — hide the bare number.
    return Number.isFinite(Number(raw)) && String(Number(raw)) === String(raw) ? "" : raw;
  }, [company.industry, industries]);

  const hasDocs = company.articleOfAssoclationUrl || company.directorIdentificationUrl;
  return (
    <Box>
      <Section icon={PersonOutlineRoundedIcon} title="Owner" classes={classes}>
        <Box className={classes.defList}>
          <DefRow label="First name" value={owner?.firstName} classes={classes} />
          <DefRow label="Last name" value={owner?.lastName} classes={classes} />
          <DefRow label="Contact" value={owner?.contact} classes={classes} mono />
          <DefRow label="Email" value={owner?.email} classes={classes} />
          <DefRow label="Timezone" value={owner?.timezone} classes={classes} />
        </Box>
      </Section>

      <Section icon={BusinessRoundedIcon} title="Company" classes={classes}>
        <Box className={classes.defList}>
          <DefRow label="Name" value={company.name} classes={classes} />
          <DefRow label="Registration no." value={company.regNo} classes={classes} mono />
          <DefRow label="Industry" value={industryName} classes={classes} />
          <DefRow label="Email" value={company.email} classes={classes} />
          <DefRow label="Contact" value={company.contact} classes={classes} mono />
          <DefRow label="Plan" value={company.plan} classes={classes} />
          <DefRow label="Status" value={company.status ? company.status.charAt(0).toUpperCase() + company.status.slice(1) : ""} classes={classes} />
        </Box>
      </Section>

      <Section icon={BusinessRoundedIcon} title="Address" classes={classes}>
        <Box className={classes.defList}>
          <DefRow label="Line 1" value={company.addressLine1} classes={classes} />
          <DefRow label="Line 2" value={company.addressLine2} classes={classes} />
          <DefRow label="City" value={company.city} classes={classes} />
          <DefRow label="State" value={company.state} classes={classes} />
          <DefRow label="Country" value={company.country} classes={classes} />
          <DefRow label="Postal code" value={company.postcode} classes={classes} mono />
        </Box>
      </Section>

      {hasDocs && (
        <Section icon={DescriptionRoundedIcon} title="Documents" classes={classes}>
          <Box className={classes.docRow}>
            {company.articleOfAssoclationUrl && (
              <Button
                variant="outlined"
                className={classes.docBtn}
                startIcon={<OpenInNewRoundedIcon style={{ fontSize: 16 }} />}
                onClick={() => window.open(company.articleOfAssoclationUrl, "_blank", "noopener,noreferrer")}
              >
                Article of Association
              </Button>
            )}
            {company.directorIdentificationUrl && (
              <Button
                variant="outlined"
                className={classes.docBtn}
                startIcon={<OpenInNewRoundedIcon style={{ fontSize: 16 }} />}
                onClick={() => window.open(company.directorIdentificationUrl, "_blank", "noopener,noreferrer")}
              >
                Director's ID
              </Button>
            )}
          </Box>
        </Section>
      )}
    </Box>
  );
}

// --- Tab 1: Configuration (White Label + API Credentials + Tags) ---
function ConfigurationTab({ company, dispatch, setSnack, onChanged, classes }) {
  return (
    <Box>
      <WhiteLabelSection company={company} dispatch={dispatch} setSnack={setSnack} onChanged={onChanged} classes={classes} />
      <ApiCredentialsSection company={company} setSnack={setSnack} classes={classes} />
      <TagsSection company={company} dispatch={dispatch} setSnack={setSnack} onChanged={onChanged} classes={classes} />
    </Box>
  );
}

function WhiteLabelSection({ company, dispatch, setSnack, onChanged, classes }) {
  const initial = company.whiteLabel === "-" ? "" : company.whiteLabel || "";
  const [whiteLabel, setWhiteLabel] = useState(initial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dirty = whiteLabel.trim() !== initial.trim();
  const hasExisting = !!initial;

  const handleSave = async () => {
    setIsSubmitting(true);
    const result = await dispatch(updateWhiteLabel(company.id, whiteLabel));
    setIsSubmitting(false);
    if (result.success) onChanged?.();
    setSnack({
      open: true,
      message: result.success ? "White label updated" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  const handleRemove = async () => {
    setIsSubmitting(true);
    const result = await dispatch(deleteCompanyWhiteLabel(company.id));
    setIsSubmitting(false);
    if (result.success) {
      setWhiteLabel("");
      onChanged?.();
    }
    setSnack({
      open: true,
      message: result.success ? "White label removed" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  return (
    <Box className={classes.subBlock}>
      <Section icon={LinkRoundedIcon} title="White Label" classes={classes}>
        <Typography className={classes.helper} style={{ marginTop: 0, marginBottom: 12 }}>
          Custom domain that masks the SmartKood URL when this company's users access the portal.
        </Typography>
        <TextField
          fullWidth
          placeholder="https://portal.example.com"
          value={whiteLabel}
          onChange={(e) => setWhiteLabel(e.target.value)}
          variant="outlined"
          size="small"
          className={classes.field}
        />
        <Box className={classes.buttonRow}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={isSubmitting || !dirty}
            className={classes.primaryBtn}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
          {hasExisting && (
            <Button
              variant="outlined"
              onClick={handleRemove}
              disabled={isSubmitting}
              className={classes.dangerBtn}
            >
              Remove
            </Button>
          )}
        </Box>
      </Section>
    </Box>
  );
}

function ApiCredentialsSection({ company, setSnack, classes }) {
  const copy = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSnack({ open: true, message: `${label} copied`, severity: "success" });
    } catch {
      setSnack({ open: true, message: "Copy failed", severity: "error" });
    }
  };

  const FieldBlock = ({ label, value }) => (
    <Box>
      <Typography className={classes.statLabel} style={{ marginBottom: 4 }}>{label}</Typography>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        value={value || "—"}
        InputProps={{
          readOnly: true,
          endAdornment: value ? (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => copy(label, value)}
                style={{ minWidth: 0, fontSize: 12, textTransform: "none", padding: "4px 8px" }}
              >
                Copy
              </Button>
            </InputAdornment>
          ) : null,
        }}
        className={classes.readonlyField}
      />
    </Box>
  );

  return (
    <Box className={classes.subBlock}>
      <Section icon={VpnKeyRoundedIcon} title="API Access Credentials" classes={classes}>
        <Typography className={classes.helper} style={{ marginTop: 0, marginBottom: 12 }}>
          Read-only credentials issued to this company for external integrations.
        </Typography>
        <Box className={classes.statRow} style={{ marginBottom: 0 }}>
          <FieldBlock label="API Access Key" value={company.api_access_key} />
          <FieldBlock label="API Access Secret" value={company.api_access_secret} />
        </Box>
      </Section>
    </Box>
  );
}

function TagsSection({ company, dispatch, setSnack, onChanged, classes }) {
  const [tags, setTags] = useState([]);
  const [initialTags, setInitialTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchCompanyTags(company.id)).then((res) => {
      const list = (res?.data?.tags ?? res?.data ?? []) || [];
      const arr = Array.isArray(list) ? list : [];
      setTags(arr);
      setInitialTags(arr);
      setIsLoading(false);
    });
  }, [company.id, dispatch]);

  const dirty = useMemo(() => {
    if (tags.length !== initialTags.length) return true;
    return tags.some((t, i) => t !== initialTags[i]);
  }, [tags, initialTags]);

  const addTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    setTags([...tags, trimmed]);
    setNewTag("");
  };

  const removeTag = (idx) => {
    setTags(tags.filter((_, i) => i !== idx));
  };

  const handleReset = () => {
    setTags(initialTags);
    setNewTag("");
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    const result = await dispatch(saveCompanyTags(company.id, tags));
    setIsSubmitting(false);
    if (result.success) {
      setInitialTags(tags);
      onChanged?.();
    }
    setSnack({
      open: true,
      message: result.success ? "Tags updated" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  return (
    <Box>
      <Section icon={LocalOfferRoundedIcon} title="Tag Configuration" classes={classes}>
        <Typography className={classes.helper} style={{ marginTop: 0, marginBottom: 12 }}>
          Free-form labels for grouping or filtering this company in reports.
        </Typography>
        <Box className={classes.tagBag}>
          {isLoading ? (
            <Typography className={classes.tagBagEmpty}>Loading…</Typography>
          ) : tags.length === 0 ? (
            <Typography className={classes.tagBagEmpty}>No tags added yet</Typography>
          ) : (
            tags.map((tag, idx) => (
              <Chip
                key={`${tag}-${idx}`}
                label={tag}
                onDelete={() => removeTag(idx)}
                size="small"
                className={classes.tagChip}
              />
            ))
          )}
        </Box>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Add a tag and press Enter"
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag();
              }
            }}
            disabled={isLoading || isSubmitting}
            className={classes.field}
          />
          <Button
            variant="outlined"
            onClick={addTag}
            disabled={!newTag.trim() || isLoading || isSubmitting}
            className={classes.ghostBtn}
            startIcon={<AddRoundedIcon style={{ fontSize: 16 }} />}
          >
            Add
          </Button>
        </Box>
        <Box className={classes.buttonRow}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleSave}
            disabled={isLoading || isSubmitting || !dirty}
            className={classes.primaryBtn}
          >
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Save tags"}
          </Button>
          {dirty && (
            <Button
              variant="outlined"
              onClick={handleReset}
              disabled={isSubmitting}
              className={classes.ghostBtn}
            >
              Reset
            </Button>
          )}
        </Box>
      </Section>
    </Box>
  );
}

// --- Tab 2: Branches ---
function BranchesTab({ company, dispatch, classes }) {
  const { nodesWithWhiteLabel, totalBranches } = useSelector((state) => state.companies);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchNodesWithWhiteLabel({ id: company.id, page: 0, length: 10 })).then(() => setIsLoading(false));
  }, [company.id, dispatch]);

  const handlePageChange = (e, newPage) => {
    setPage(newPage);
    setIsLoading(true);
    dispatch(fetchNodesWithWhiteLabel({ id: company.id, page: newPage, length: 10 })).then(() => setIsLoading(false));
  };

  const isEmpty = !isLoading && (!nodesWithWhiteLabel || nodesWithWhiteLabel.length === 0);

  return (
    <Section icon={StorefrontRoundedIcon} title="Branches" classes={classes}>
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: "hidden" }}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Branch</TableCell>
              <TableCell>White Label</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`bs-${i}`}>
                  <TableCell><Skeleton variant="text" width={140} /></TableCell>
                  <TableCell><Skeleton variant="text" width={200} /></TableCell>
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Box className={classes.emptyWrap}>
                    <StorefrontRoundedIcon className={classes.emptyIcon} />
                    <Typography className={classes.emptyTitle}>No branches yet</Typography>
                    <Typography className={classes.emptyHint}>This company hasn't created any branches.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              nodesWithWhiteLabel.map((branch) => (
                <TableRow key={branch.uuid || branch.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{branch.name}</TableCell>
                  <TableCell sx={{ color: branch.white_label ? tokens.text : tokens.textMuted }}>
                    {branch.white_label || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty && (
        <TablePagination
          component="div"
          count={totalBranches || 0}
          page={page}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
          onPageChange={handlePageChange}
          className={classes.pagination}
        />
      )}
    </Section>
  );
}

// --- Tab 3: Users ---
function UsersTab({ company, dispatch, classes }) {
  const { companyUsers, totalUsers, usersLoading } = useSelector((state) => state.companies);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchCompanyUsers({ companyId: company.id, companyPlan: company.plan, page: 0, length: 10 }));
  }, [company.id, company.plan, dispatch]);

  const runSearch = (nextPage = 0) => {
    setPage(nextPage);
    dispatch(fetchCompanyUsers({ companyId: company.id, companyPlan: company.plan, page: nextPage, length: 10, query: search }));
  };

  const isEmpty = !usersLoading && (!companyUsers || companyUsers.length === 0);

  return (
    <Section icon={PeopleAltRoundedIcon} title="Users" classes={classes}>
      <Box display="flex" gap={1} mb={2}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by name, email, or username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(0); }}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" htmlColor={tokens.textMuted} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          disableElevation
          onClick={() => runSearch(0)}
          className={classes.primaryBtn}
        >
          Search
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${tokens.border}`, borderRadius: 2, overflow: "hidden" }}>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`us-${i}`}>
                  <TableCell><Skeleton variant="text" width={100} /></TableCell>
                  <TableCell><Skeleton variant="text" width={160} /></TableCell>
                  <TableCell><Skeleton variant="text" width={120} /></TableCell>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell align="right"><Skeleton variant="text" width={40} sx={{ ml: "auto" }} /></TableCell>
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box className={classes.emptyWrap}>
                    <PeopleAltRoundedIcon className={classes.emptyIcon} />
                    <Typography className={classes.emptyTitle}>No users found</Typography>
                    <Typography className={classes.emptyHint}>
                      {search ? "Try a different search term." : "This company has no users yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              companyUsers.map((user) => {
                const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ");
                return (
                  <TableRow key={user.id || user.uuid} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{user.username || "—"}</TableCell>
                    <TableCell sx={{ color: tokens.textDim }}>{user.email || "—"}</TableCell>
                    <TableCell>{fullName || "—"}</TableCell>
                    <TableCell sx={{ color: tokens.textDim }}>{user.role_name || user.role || "—"}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {user.credit ?? user.qr_credit ?? "—"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty && (
        <TablePagination
          component="div"
          count={totalUsers || 0}
          page={page}
          rowsPerPage={10}
          rowsPerPageOptions={[10]}
          onPageChange={(e, newPage) => runSearch(newPage)}
          className={classes.pagination}
        />
      )}
    </Section>
  );
}

// --- Tab 4: Expiry ---
const PRESET_MONTHS = [3, 6, 12, 24];

function ExpiryTab({ company, dispatch, setSnack, onChanged, classes }) {
  const [monthsInput, setMonthsInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const newDate = useMemo(() => {
    if (!monthsInput || isNaN(monthsInput)) return "";
    const months = parseInt(monthsInput, 10);
    if (months <= 0) return "";
    const baseDate = company.expired_at ? new Date(company.expired_at) : new Date();
    const now = new Date();
    const startDate = baseDate > now ? baseDate : now;
    const currentDay = startDate.getDate();
    startDate.setMonth(startDate.getMonth() + months);
    const year = startDate.getFullYear();
    const month = startDate.getMonth() + 1;
    const lastDay = new Date(year, month, 0).getDate();
    const day = Math.min(currentDay, lastDay);
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }, [monthsInput, company.expired_at]);

  const handleSave = async () => {
    if (!newDate) return;
    setIsSubmitting(true);
    const result = await dispatch(saveCompanyExpiredDate(company.id, newDate));
    setIsSubmitting(false);
    setSnack({
      open: true,
      message: result.success ? "Expiry date updated" : result.message,
      severity: result.success ? "success" : "error",
    });
    if (result.success) {
      setMonthsInput("");
      onChanged?.();
    }
  };

  const activePreset = PRESET_MONTHS.find((p) => String(p) === monthsInput) ?? null;

  return (
    <Section icon={EventBusyRoundedIcon} title="Expiry & Deactivation" classes={classes}>
      <Box className={classes.statRow}>
        <Box className={classes.stat}>
          <Typography className={classes.statLabel}>Current Expiry</Typography>
          <Typography
            className={`${classes.statValue} ${!company.expired_at ? classes.statValueDim : ""}`}
          >
            {company.expired_at || "Not set"}
          </Typography>
        </Box>
        <Box className={classes.stat}>
          <Typography className={classes.statLabel}>Deactivation Date</Typography>
          <Typography
            className={`${classes.statValue} ${!company.deactive_at ? classes.statValueDim : ""}`}
          >
            {company.deactive_at || "Not set"}
          </Typography>
        </Box>
      </Box>

      <Typography className={classes.sectionTitle} style={{ marginBottom: 10 }}>
        Extend by
      </Typography>
      <Box className={classes.presetRow}>
        {PRESET_MONTHS.map((m) => {
          const active = activePreset === m;
          return (
            <Chip
              key={m}
              label={`${m} months`}
              clickable
              onClick={() => setMonthsInput(String(m))}
              className={`${classes.presetChip} ${active ? classes.presetChipActive : ""}`}
            />
          );
        })}
      </Box>
      <TextField
        type="number"
        size="small"
        variant="outlined"
        value={monthsInput}
        onChange={(e) => setMonthsInput(e.target.value)}
        placeholder="Custom (months)"
        inputProps={{ min: 1, step: 1, "aria-label": "Custom months" }}
        className={classes.field}
        sx={{ width: 200 }}
      />

      {newDate && (
        <Box className={classes.preview}>
          <Typography className={classes.previewLabel}>New expiry</Typography>
          <Typography className={classes.previewValue}>
            {company.expired_at || "today"}
          </Typography>
          <ArrowForwardRoundedIcon className={classes.previewArrow} />
          <Typography className={classes.previewValue}>{newDate}</Typography>
        </Box>
      )}

      <Box className={classes.buttonRow}>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={isSubmitting || !newDate}
          className={classes.primaryBtn}
        >
          {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Save expiry"}
        </Button>
        {monthsInput && (
          <Button
            variant="outlined"
            onClick={() => setMonthsInput("")}
            disabled={isSubmitting}
            className={classes.ghostBtn}
          >
            Reset
          </Button>
        )}
      </Box>
    </Section>
  );
}

// --- Tab 2: Features ---
function FeaturesTab({ company, dispatch, setSnack, onChanged, classes }) {
  const { featureGroupOptions, companyFeature, featureStructure } = useSelector((state) => state.companies);
  const featurePanelRef = useRef(null);

  const [subTab, setSubTab] = useState(0);
  const [featureGroup, setFeatureGroup] = useState(-1);
  const [features, setFeatures] = useState(null);
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(fetchFeatureGroupOptions()),
      dispatch(fetchCompanyFeature(company.id)),
      dispatch(fetchFeatureStructure()),
    ]).then(() => setIsLoading(false));
  }, [company.id, dispatch]);

  useEffect(() => {
    if (companyFeature) {
      setFeatureGroup(companyFeature.featureGroupId ?? -1);
      setProperties(companyFeature.properties || []);
    }
  }, [companyFeature]);

  const hasFeatureGroup = featureGroup !== undefined && featureGroup !== -1;

  const handleSave = async () => {
    setIsSubmitting(true);
    const payload = {
      featureGroupId: hasFeatureGroup ? featureGroup : null,
      features: hasFeatureGroup ? null : features,
      properties: properties.map((p) => ({ name: p.name, label: p.label, value: p.value ?? "" })),
    };
    const result = await dispatch(saveCompanyFeature(company.id, payload));
    setIsSubmitting(false);
    if (result.success) onChanged?.();
    setSnack({
      open: true,
      message: result.success ? "Features updated" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  const updateProperty = (name, label, key, value) => {
    setProperties((prev) => {
      const idx = prev.findIndex((p) => p.name === name);
      const next = [...prev];
      if (idx >= 0) next[idx] = { ...next[idx], [key]: value };
      else next.push({ name, label, [key]: value });
      return next;
    });
  };

  const propValue = (name, key = "value") =>
    properties.find((p) => p.name === name)?.[key] ?? "";

  return (
    <Section icon={TuneRoundedIcon} title="Features" classes={classes}>
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={22} sx={{ color: tokens.brand }} />
        </Box>
      ) : !companyFeature || !featureStructure ? (
        <Box className={classes.emptyWrap}>
          <InfoOutlinedIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyTitle}>Features unavailable</Typography>
          <Typography className={classes.emptyHint}>
            You may not have permission to view feature configuration for this company.
          </Typography>
        </Box>
      ) : (
        <>
          <Box mb={2}>
            <Typography className={classes.statLabel} style={{ marginBottom: 6 }}>
              Feature Group
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              value={featureGroup}
              onChange={(e) => setFeatureGroup(Number(e.target.value))}
              className={classes.field}
            >
              <MenuItem value={-1}><em>None (custom configuration)</em></MenuItem>
              {featureGroupOptions?.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Tabs
            value={subTab}
            onChange={(e, v) => setSubTab(v)}
            className={classes.subTabs}
          >
            <Tab label="Features" />
            <Tab label="Properties" />
          </Tabs>

          {subTab === 0 && (
            <Box>
              {!hasFeatureGroup && (
                <Box className={classes.toolRow}>
                  <Button
                    size="small"
                    onClick={() => featurePanelRef.current?.selectAll()}
                    className={classes.textBtnPrimary}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    onClick={() => featurePanelRef.current?.deselectAll()}
                    className={classes.textBtnDanger}
                  >
                    Deselect All
                  </Button>
                </Box>
              )}
              <Box className={classes.featurePanelWrap}>
                {!hasFeatureGroup ? (
                  <CompanyFeaturePanel
                    ref={featurePanelRef}
                    key={JSON.stringify(companyFeature.enabledAccessRight)}
                    features={featureStructure}
                    onDataChange={(value) => setFeatures(value)}
                    layer={5}
                    enabledList={companyFeature.enabledAccessRight}
                  />
                ) : (
                  <CompanyFeaturePanel
                    key={`group-${featureGroup}`}
                    features={featureStructure}
                    onDataChange={(value) => setFeatures(value)}
                    disabled
                    layer={5}
                    enabledList={
                      featureGroupOptions?.find((g) => g.id === featureGroup)?.enabled
                    }
                  />
                )}
              </Box>
            </Box>
          )}

          {subTab === 1 && (
            <Box>
              {!properties || properties.length === 0 ? (
                <Box className={classes.emptyWrap}>
                  <InfoOutlinedIcon className={classes.emptyIcon} />
                  <Typography className={classes.emptyTitle}>No properties configured</Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {hasFeatureGroup && (
                    <Box
                      display="grid"
                      gridTemplateColumns="180px 1fr 1fr"
                      gap={1.5}
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: tokens.textDim,
                        pb: 1,
                        borderBottom: `1px solid ${tokens.border}`,
                      }}
                    >
                      <Box>Property</Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        Company Setting
                        <Tooltip title="Company Setting overrides Feature Group Setting if filled in." arrow>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: tokens.textMuted }} />
                        </Tooltip>
                      </Box>
                      <Box>Feature Group Setting</Box>
                    </Box>
                  )}
                  {companyFeature.properties.map((property) => {
                    const isNumber = property.type === 2;
                    const inputType = isNumber ? "number" : "text";
                    const inputProps = isNumber ? { step: "any", min: 0 } : {};
                    return (
                      <Box
                        key={property.name}
                        display="grid"
                        gridTemplateColumns={hasFeatureGroup ? "180px 1fr 1fr" : "180px 1fr"}
                        gap={1.5}
                        alignItems="center"
                      >
                        <Typography className={classes.propertyLabel}>
                          {property.label || property.name}
                        </Typography>
                        <TextField
                          size="small"
                          variant="outlined"
                          type={inputType}
                          inputProps={inputProps}
                          placeholder={hasFeatureGroup ? "Company value (optional)" : "Value unset"}
                          value={propValue(property.name, "value")}
                          onChange={(e) =>
                            updateProperty(property.name, property.label, "value", e.target.value)
                          }
                          className={classes.field}
                        />
                        {hasFeatureGroup && (
                          <TextField
                            size="small"
                            variant="outlined"
                            type={inputType}
                            inputProps={{ ...inputProps, readOnly: true }}
                            placeholder="Feature group value"
                            value={propValue(property.name, "feature_group_value")}
                            className={classes.readonlyField}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          <Box className={classes.buttonRow}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSave}
              disabled={isSubmitting}
              className={classes.primaryBtn}
            >
              {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Save features"}
            </Button>
          </Box>
        </>
      )}
    </Section>
  );
}

// --- Tab 6: Sales Email ---
function SalesEmailTab({ company, dispatch, setSnack, onChanged, classes }) {
  const initial = company.sales_email || "";
  const [email, setEmail] = useState(initial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dirty = email.trim() !== initial.trim();
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSave = async () => {
    if (!isValidEmail) {
      setSnack({ open: true, message: "Enter a valid email address", severity: "error" });
      return;
    }
    setIsSubmitting(true);
    const result = await dispatch(saveSalesEmail(company.id, email.trim()));
    setIsSubmitting(false);
    if (result.success) onChanged?.();
    setSnack({
      open: true,
      message: result.success ? "Sales email updated" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  return (
    <Section icon={EmailRoundedIcon} title="Sales Email" classes={classes}>
      <Typography className={classes.helper} style={{ marginTop: 0, marginBottom: 12 }}>
        Internal contact for plan upgrades, renewals, and billing escalations.
      </Typography>
      <TextField
        fullWidth
        type="email"
        placeholder="sales@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        variant="outlined"
        size="small"
        className={classes.field}
        error={dirty && email.length > 0 && !isValidEmail}
        helperText={
          dirty && email.length > 0 && !isValidEmail
            ? "Doesn't look like a valid email"
            : ""
        }
        FormHelperTextProps={{ sx: { fontSize: 12, marginLeft: 0 } }}
      />
      <Box className={classes.buttonRow}>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={isSubmitting || !dirty || !isValidEmail}
          className={classes.primaryBtn}
        >
          {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Save"}
        </Button>
      </Box>
    </Section>
  );
}

// --- Tab 7: Swap Plan ---
function SwapPlanTab({ company, dispatch, setSnack, onChanged, classes }) {
  const { planInfo, plans } = useSelector((state) => state.companies);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [keepAccessRights, setKeepAccessRights] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      dispatch(fetchCompanyPlanInfo(company.id)),
      dispatch(fetchAvailablePlans()),
    ]).then(() => setIsLoading(false));
  }, [company.id, dispatch]);

  useEffect(() => {
    if (plans?.length && !selectedPlan) {
      setSelectedPlan(plans[0].uuid);
    }
  }, [plans, selectedPlan]);

  const handleSubmit = async () => {
    if (!selectedPlan) return;
    setIsSubmitting(true);
    const result = await dispatch(swapCompanyPlan(company.id, selectedPlan, !keepAccessRights));
    setIsSubmitting(false);
    if (result.success) onChanged?.();
    setSnack({
      open: true,
      message: result.success ? "Plan swapped" : result.message,
      severity: result.success ? "success" : "error",
    });
  };

  const noPlans = !isLoading && (!plans || plans.length === 0);

  return (
    <Section icon={SwapHorizRoundedIcon} title="Swap Plan" classes={classes}>
      <Typography className={classes.helper} style={{ marginTop: 0, marginBottom: 16 }}>
        Move this company to a different subscription plan. Optionally preserve their existing access rights.
      </Typography>

      {isLoading ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Skeleton variant="rounded" height={64} />
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={32} width={240} />
        </Box>
      ) : noPlans ? (
        <Box className={classes.emptyWrap}>
          <InfoOutlinedIcon className={classes.emptyIcon} />
          <Typography className={classes.emptyTitle}>No plans available</Typography>
          <Typography className={classes.emptyHint}>
            You may not have permission to view plan options.
          </Typography>
        </Box>
      ) : (
        <>
          <Box className={classes.stat} style={{ marginBottom: 18 }}>
            <Typography className={classes.statLabel}>Current Plan</Typography>
            <Typography
              className={`${classes.statValue} ${!planInfo?.plan_name ? classes.statValueDim : ""}`}
              style={{ textTransform: "capitalize" }}
            >
              {planInfo?.plan_name || company.plan || "—"}
            </Typography>
          </Box>

          <Typography className={classes.statLabel} style={{ marginBottom: 6 }}>
            New Plan
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            variant="outlined"
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            className={classes.field}
            disabled={isSubmitting}
          >
            {plans.map((p) => (
              <MenuItem key={p.uuid} value={p.uuid} sx={{ textTransform: "capitalize", fontSize: 13 }}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            sx={{ mt: 2, ml: -1 }}
            control={
              <Checkbox
                checked={keepAccessRights}
                onChange={(e) => setKeepAccessRights(e.target.checked)}
                sx={{
                  color: tokens.textDim,
                  "&.Mui-checked": { color: tokens.brand },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: 13, color: tokens.text }}>
                Keep current access rights when switching plan
              </Typography>
            }
          />

          <Box className={classes.buttonRow}>
            <Button
              variant="contained"
              disableElevation
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedPlan}
              className={classes.primaryBtn}
            >
              {isSubmitting ? <CircularProgress size={18} color="inherit" /> : "Update plan"}
            </Button>
          </Box>
        </>
      )}
    </Section>
  );
}
